import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useStateSelector } from 'src/redux';
import { Poppins } from 'src/common/Poppins';
import { pulseHighlight } from 'src/common/pulseHighlight';
import { GradientTextAction } from 'src/common/GradientText';
import { formatCurrency } from 'src/utils/misc';
import { CurrencyInputFormula } from 'src/components/form/CurrencyInput';
import Input from 'src/components/form/Input';
import { CarbonFlowSvg } from 'src/assets/dynamicSvg';
import { IntelVariable } from '../types';

// Subtle, professional entrance: fade + slight rise/scale with an ease-out curve.
const popIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-6px) scale(0.985);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const Card = styled.div<{ $maxHeight?: number }>`
  width: min(600px, calc(100vw - 24px));
  display: flex;
  flex-direction: column;
  ${({ $maxHeight }) => $maxHeight != null && `max-height: ${$maxHeight}px;`}
  background: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 37px 0 rgba(7, 46, 91, 0.12);
  transform-origin: top left;
  animation: ${popIn} 0.16s cubic-bezier(0.16, 1, 0.3, 1);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  flex-shrink: 0;
`;

// The variable rows scroll inside the bounded card; the header stays put.
const List = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

// The gradient base renders bold; lighten it to a medium weight. Subtle fade on hover.
const HeaderAction = styled(GradientTextAction)`
  font-weight: 500;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.7;
  }
`;

// "Add Value" row action — same subtle hover as the header actions.
const AddValueAction = styled(GradientTextAction)`
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const Row = styled.div<{ $active?: boolean; $pulse?: boolean; $selectable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  /* Compact by default; roomier while editing so the value editor isn't cramped. */
  padding: ${({ $active }) => ($active ? '12px 24px' : '8px 24px')};
  border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
  background: ${({ theme, $active }) => ($active ? theme.colors.cultured : 'transparent')};
  cursor: ${({ $selectable }) => ($selectable ? 'pointer' : 'default')};
  transition: background 0.12s ease;

  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: ${({ theme }) => theme.colors.cultured};
  }

  /* Pulsating attention ring for the click-through target row. Inset positive so it rings
     just inside the row edge — the card clips overflow, so an outset ring would be cut off. */
  ${({ $pulse }) => $pulse && pulseHighlight('&', { inset: 3, radius: 6 })}
`;

const Name = styled(Poppins)`
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Editor = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CurrencyField = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const IconButton = styled.button<{ $gradient?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 1000px;
  cursor: pointer;
  background: ${({ theme, $gradient }) => ($gradient ? theme.colors.gradient : theme.colors.white)};
  border: ${({ theme, $gradient }) => ($gradient ? 'none' : `1px solid ${theme.colors.cflowerBlue}`)};

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: none;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.gp2};
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: rgba(7, 46, 91, 0.06);
    color: ${({ theme }) => theme.colors.gp1};
  }
`;

// The per-type variable icons (getVariableIcon) are ~10px natively — scale them up a little
// and centre them so they read clearly without dominating the row.
const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;

  svg {
    transform: scale(1.6);
  }
`;

interface VariablesPopoverProps {
  variables: IntelVariable[];
  pending: boolean;
  onPopulate: (variableId: number, value: number, currency: boolean) => void;
  onAddWorkspaceVariable: () => void;
  onAddAssessmentVariable: () => void;
  /** When set, open straight into this variable's editor (a red formula chip was clicked). */
  initialEditId?: number | null;
  /** Caps the card height to the space available in the viewport; the list scrolls within. */
  maxHeight?: number;
  /** Override the leading row icon per variable (e.g. per-type icons in the scenario form). */
  renderIcon?: (variable: IntelVariable) => React.ReactNode;
  /** When set, clicking a (non-editing) row picks that variable — used by the `#` insert flow. */
  onSelect?: (variable: IntelVariable) => void;
  /** Notified when an inline value edit opens/closes — lets a host keep the popup alive on blur. */
  onEditingChange?: (editing: boolean) => void;
}

export const VariablesPopover: React.FC<VariablesPopoverProps> = ({
  variables,
  pending,
  onPopulate,
  onAddWorkspaceVariable,
  onAddAssessmentVariable,
  initialEditId,
  maxHeight,
  renderIcon,
  onSelect,
  onEditingChange,
}) => {
  const colors = useStateSelector((state) => state.theme.colors);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<number | undefined>(undefined);
  const [draftCurrency, setDraftCurrency] = useState(true);
  const rowRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const startEdit = (variable: IntelVariable) => {
    onEditingChange?.(true); // sync: keep a host popup alive when the editor blurs as the input focuses
    setEditingId(variable.id);
    setDraft(variable.value ?? undefined);
    setDraftCurrency(variable.currency);
  };

  const cancelEdit = () => {
    setEditingId(null);
    onEditingChange?.(false);
  };

  // Arrive-at-target: open the row's editor (autofocuses the value input) and scroll it into
  // view within the card. The row also pulses while it's the target (see Row `$pulse`).
  useEffect(() => {
    if (initialEditId == null) return;
    const variable = variables.find((v) => v.id === initialEditId);
    if (variable) startEdit(variable);
    const t = setTimeout(() => {
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      rowRefs.current[initialEditId]?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
    }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEditId]);

  const confirm = () => {
    const id = editingId;
    const value = draft;
    const currency = draftCurrency;
    // Clear editing state BEFORE onPopulate: in the `#` flow onPopulate inserts a chip, which ends
    // the suggestion and fires its onExit — that handler must see editing=false so it can clean up.
    setEditingId(null);
    onEditingChange?.(false);
    if (id != null && value != null) onPopulate(id, value, currency);
  };

  return (
    <Card $maxHeight={maxHeight}>
      <Header>
        <Poppins px={16} weight={500} color="cflowerBlue" css="text-transform: uppercase;">
          Variables
        </Poppins>
        <Actions>
          <HeaderAction px={14} onClick={onAddWorkspaceVariable}>
            + Workspace variable
          </HeaderAction>
          <HeaderAction px={14} onClick={onAddAssessmentVariable}>
            + Assessment variable
          </HeaderAction>
        </Actions>
      </Header>

      <List>
      {variables.map((variable) => {
        const editing = editingId === variable.id;
        return (
          <Row
            key={variable.id}
            ref={(el) => {
              rowRefs.current[variable.id] = el;
            }}
            $active={editing}
            $pulse={variable.id === initialEditId}
            $selectable={!!onSelect && !editing}
            onClick={onSelect && !editing ? () => onSelect(variable) : undefined}
          >
            {renderIcon ? (
              <IconSlot>{renderIcon(variable)}</IconSlot>
            ) : (
              <CarbonFlowSvg size={20} color={variable.scope === 'workspace' ? colors.brightBlue : undefined} />
            )}
            <Name px={14} weight={500} color="cflowerBlue">
              {variable.name}
            </Name>
            {editing ? (
              <Editor>
                <CurrencyField>
                  <Poppins px={14} weight={500} color="cflowerBlue">
                    Currency
                  </Poppins>
                  <Input
                    type="checkbox"
                    className="switch switch--small"
                    checked={draftCurrency}
                    onChange={(e) => setDraftCurrency(e.target.checked)}
                    aria-label="Currency"
                  />
                </CurrencyField>
                <CurrencyInputFormula
                  currency={draftCurrency}
                  value={draft ?? ''}
                  autoFocus
                  onValueChange={({ floatValue }) => setDraft(floatValue)}
                />
                <IconButton
                  type="button"
                  aria-label="Save value"
                  $gradient
                  disabled={pending || draft == null}
                  onClick={confirm}
                >
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M4.16667 9.93083L8.10333 13.75L15.8333 6.25"
                      stroke="#FFFFFF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </IconButton>
                <IconButton type="button" aria-label="Cancel" onClick={cancelEdit}>
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M5 5L15 15M15 5L5 15"
                      stroke={colors.cflowerBlue}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </IconButton>
              </Editor>
            ) : variable.value != null ? (
              <ValueRow>
                <Poppins px={14} weight={500} color="prussianBlue">
                  {variable.currency ? formatCurrency(variable.value) : variable.value.toLocaleString()}
                </Poppins>
                <EditButton
                  type="button"
                  aria-label={`Edit ${variable.name}`}
                  onClick={(e) => {
                    e.stopPropagation(); // don't let the row's onSelect (insert) fire
                    startEdit(variable);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M13.5 3.5L16.5 6.5M3 17H6L15.5 7.5C16.3 6.7 16.3 5.3 15.5 4.5C14.7 3.7 13.3 3.7 12.5 4.5L3 14V17Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </EditButton>
              </ValueRow>
            ) : (
              <ValueRow>
                <AddValueAction
                  px={14}
                  onClick={(e) => {
                    e.stopPropagation(); // don't let the row's onSelect (insert) fire
                    startEdit(variable);
                  }}
                  weight={500}
                >
                  Add Value
                </AddValueAction>
                <EditButton
                  type="button"
                  aria-label={`Add value for ${variable.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    startEdit(variable);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M13.5 3.5L16.5 6.5M3 17H6L15.5 7.5C16.3 6.7 16.3 5.3 15.5 4.5C14.7 3.7 13.3 3.7 12.5 4.5L3 14V17Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </EditButton>
              </ValueRow>
            )}
          </Row>
        );
      })}
      </List>
    </Card>
  );
};
